import {
  FilterList as FilterListIcon,
  PersonSearch as PersonSearchIcon,
} from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  Container,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid, GridSortModel } from '@mui/x-data-grid';
import { useSnackbar } from 'notistack';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { WrappedTask } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Task';
import { TaskResponse } from 'src/@types/crs/task';
import { PaginateQuery } from 'src/api/pagination/dtos';
import FilterDrawer from 'src/components/FilterDrawer';
import HeaderBreadcrumbs from 'src/components/HeaderBreadcrumbs';
import Page from 'src/components/Page';
import PeriodFilter, { onDateChange } from 'src/components/PeriodFilter';
import SearchTextField from 'src/components/SearchTextField';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import useObjectState from 'src/hooks/useObjectState';
import useSettings from 'src/careflow/ph-core/multi-tenant/setting/useSettings';
import {
  Reference,
  ValueSetComposeIncludeConcept,
} from 'src/careflow/ph-core/fhir-client/resources';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { updateTask, getTasks, getTasksAndStats } from './task.api';
import { debounce } from 'src/utils/timers';
import SearchMemberModal from 'src/careflow/care-coordination/case/detail/components/SearchMember/SearchMemberModal';
import TaskDetailDrawer from '../common/TaskDetailDrawer';
import getTaskListColumns from './taskColumns';
import useTenantConfigData from 'src/careflow/ph-core/multi-tenant/tenant/hook/useTenantConfigData';
import useValueSetsByIdentifiers from 'src/hooks/useValueSetsByIdentifier';
import { onSelectResource } from 'src/careflow/care-coordination/case/detail/components/SearchMember/SearchMember';
import ModalCommunication from 'src/careflow/common/ModalCommunication';
import CustomDataGridPagination from 'src/careflow/common/CustomDataGridPagination';
import { defaultRowsPerPageOptions } from 'src/careflow/helpers/constants';
import { TaskPermissions } from '../../../helpers/types';
import { apiRoutes } from '../../../../utils/apiConfig';
import { fhirClient } from '../../../../App';
import { isArray } from 'lodash';
import {
  PatientWrapper,
  WrappedPatient,
} from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import { getOwnerDisplayLabel } from '../patient/TasksGrid/TaskModal';
import { commonMenuTaskHandlers } from '../common/handlers';
import TaskContextProvider from '../common/provider';
import { TaskModalResolver } from '../patient/TasksGrid/TaskModalResolver';

export interface GridState {
  page: number;
  rowsPerPage: number;
  orderBy: string | null;
  orderDirection: 'asc' | 'desc' | null;
  search: string;
  statusFilter: ValueSetComposeIncludeConcept[];
  typeFilter: ValueSetComposeIncludeConcept[];
  ownerFilter: Reference[];
}

interface TaskDetailDrawerState {
  selectedTaskId: string;
  isTaskDetailDrawerOpen: boolean;
}

interface TaskEditModalState {
  taskToEdit: WrappedTask | null;
  isTaskEditModalOpen: boolean;
  isSearchMemberModalOpen: boolean;
  isAssignProgramModalOpen: boolean;
  addNoteToTask: boolean;
  subTaskBasedOn: WrappedTask | null;
  patientFromTask?: WrappedPatient | null;
}

export interface TaskFilters {
  selectedOwners: Reference[];
  startDate: moment.Moment | null;
  endDate: moment.Moment | null;
}
export const initialTaskFilters: TaskFilters = {
  selectedOwners: [],
  startDate: null,
  endDate: null,
};

interface TaskListProps {
  includeBreadcrumbs?: boolean;
  list: TaskResponse[];
  setList: Dispatch<SetStateAction<TaskResponse[]>>;
  searchValue: string;
  setSearchValue: Dispatch<SetStateAction<string>>;
  totalItems: number;
  setTotalItems: Dispatch<SetStateAction<number>>;
  startDate: any;
  setStartDate: Dispatch<any>;
  isLoading: boolean;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  initialGridState: GridState;
  includeStats?: boolean;
  setStats?: Dispatch<SetStateAction<any>>;
  taskProgressStatus?: string;
  show?: boolean;
  ownerFilterUser?: Reference[];
  ownerRequired?: boolean;
  permissions: TaskPermissions;
  showUnassignedOnly?: boolean;
  showAssignedOnly?: boolean;
}

const TaskList = ({
  includeBreadcrumbs = true,
  list,
  setList,
  searchValue,
  setSearchValue,
  totalItems,
  setTotalItems,
  startDate,
  setStartDate,
  isLoading,
  setIsLoading,
  initialGridState,
  includeStats = false,
  setStats,
  taskProgressStatus,
  show = true,
  ownerFilterUser,
  ownerRequired = false,
  permissions,
  showUnassignedOnly = false,
  showAssignedOnly = false,
}: TaskListProps) => {
  const { themeStretch } = useSettings();
  const [isSearchMemberFilterModalOpen, setIsSearchMemberFilterModalOpen] =
    useState<boolean>(false);
  const [isOpenAplyFilters, setIsOpenAplyFilters] = useState<boolean>(false);
  const [endDate, setEndDate] = useState<any>('');
  const { i18n } = useLocales();
  const { enqueueSnackbar } = useSnackbar();
  const firstRun = useRef(true);
  const { componentsData } = useTenantConfigData();
  const { searchMemberOptions } = componentsData?.searchMember ?? {};

  const [gridState, updateGridState] = useObjectState<GridState>(initialGridState);
  const [pageInput, setPageInput] = useState('');
  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: initialGridState.orderBy || 'id',
      sort: initialGridState.orderDirection,
    },
  ]);
  const hasResetRef = useRef(false);

  useEffect(() => {
    if (initialGridState?.statusFilter !== undefined) {
      updateGridState({
        statusFilter: initialGridState.statusFilter,
      });
    }
  }, [initialGridState?.statusFilter]);

  const [{ selectedTaskId, isTaskDetailDrawerOpen }, updateDrawerState] =
    useObjectState<TaskDetailDrawerState>({
      selectedTaskId: '',
      isTaskDetailDrawerOpen: false,
    });

  const [
    {
      taskToEdit,
      isTaskEditModalOpen,
      isSearchMemberModalOpen,
      subTaskBasedOn,
      addNoteToTask,
      patientFromTask,
    },
    updateEditModalState,
  ] = useObjectState<TaskEditModalState>({
    taskToEdit: null,
    isTaskEditModalOpen: false,
    isSearchMemberModalOpen: false,
    isAssignProgramModalOpen: false,
    addNoteToTask: false,
    subTaskBasedOn: null,
  });

  const {
    valueSets: [taskStatusRecords, taskTypesRecords],
  } = useValueSetsByIdentifiers(['ph-task-status', 'ph-task-type-codes']);

  const handleGetPatient = async (patientId) => {
    const url = `${apiRoutes.getFhirPatientById}${patientId}`;
    const response: any = await fhirClient.search(url);
    if (response && response?.entry && isArray(response.entry) && response.entry.length > 0) {
      const fhirPatient = response?.entry?.[0]?.resource;
      updateEditModalState({
        patientFromTask: PatientWrapper(fhirPatient),
      });
    } else {
      updateEditModalState({
        patientFromTask: null,
      });
    }
  };

  useEffect(() => {
    const patientId = taskToEdit?.for?.reference ? taskToEdit?.getPatientId() ?? null : null;

    if (patientId) {
      handleGetPatient(patientId);
    } else {
      updateEditModalState({
        patientFromTask: null,
      });
    }
  }, [taskToEdit]);

  const taskStatus = useMemo(() => taskStatusRecords ?? null, [taskStatusRecords]);
  const taskTypes = useMemo(() => taskTypesRecords ?? null, [taskTypesRecords]);

  const handleOnTaskEdit = (task: WrappedTask) => {
    updateDrawerState({ isTaskDetailDrawerOpen: false });
    updateEditModalState({
      taskToEdit: task,
      isTaskEditModalOpen: true,
    });
  };

  const handleOnSearchMemberOpen = (task: WrappedTask) => {
    updateEditModalState({
      taskToEdit: task,
      isSearchMemberModalOpen: true,
    });
  };

  const handleOnTaskCopy = (task: WrappedTask) => {
    const { id, ...rest } = task;
    const taskCopy = { ...rest };
    updateEditModalState({ taskToEdit: taskCopy, isTaskEditModalOpen: true });
  };

  const handleOnSubTask = (task: WrappedTask) => {
    updateEditModalState({ subTaskBasedOn: task, isTaskEditModalOpen: true });
  };

  const handleAddNoteToTask = (task: WrappedTask) => {
    updateEditModalState({ taskToEdit: task, addNoteToTask: true });
  };

  const handleSearchChange = useMemo(
    () =>
      debounce((search: string) => {
        updateGridState({ search });
      }, 600),
    []
  );

  const handleTaskDetailsOpen = useCallback((fhirId: string) => {
    updateDrawerState({
      isTaskDetailDrawerOpen: true,
      selectedTaskId: fhirId,
    });
  }, []);

  const fetchTaskList = useCallback(async () => {
    setIsLoading(true);
    const {
      page,
      rowsPerPage,
      orderBy,
      search,
      orderDirection,
      statusFilter,
      typeFilter,
      ownerFilter,
    } = gridState;
    const paginateQuery: PaginateQuery = {
      page: page,
      limit: rowsPerPage,
      sortBy: orderBy && orderDirection ? [[orderBy, orderDirection.toUpperCase()]] : [],
    };

    if (searchValue) {
      paginateQuery.search = searchValue;
    }

    paginateQuery.filter = {};

    if (statusFilter.length) {
      paginateQuery.filter.status = `$in:${statusFilter.map(({ code }) => code).join(',')}`;
    }

    if (typeFilter.length) {
      paginateQuery.filter.taskType = `$in:${typeFilter.map(({ code }) => code).join(',')}`;
    }

    if (startDate) {
      paginateQuery.filter.periodStart = `$gte:${startDate.format('YYYY-MM-DD')}`;
    }

    if (endDate) {
      paginateQuery.filter.periodEnd = `$lte:${endDate.format('YYYY-MM-DD')}`;
    }

    if (showUnassignedOnly) {
      paginateQuery.filter.ownerFhirId = '$null';
    } else if (showAssignedOnly) {
      paginateQuery.filter.ownerFhirId = '$notnull';
    } else if (ownerFilter && ownerFilter.length > 0) {
      paginateQuery.filter.ownerFhirId = `$in:${ownerFilter
        .map(({ reference }) => reference)
        .join(',')}`;
    }

    if (ownerRequired && ownerFilter.length === 0 && !showUnassignedOnly && !showAssignedOnly) {
      if (!firstRun.current) {
        enqueueSnackbar('Task owner is required, try pressing clear all button to reset filters', {
          variant: 'error',
        });
      } else {
        firstRun.current = false;
      }

      setIsLoading(false);
      return;
    }

    if (taskProgressStatus) {
      paginateQuery.filter.taskProgressStatus = taskProgressStatus;
    }

    const response = includeStats
      ? await getTasksAndStats(paginateQuery)
      : await getTasks(paginateQuery);
    const data = response?.data;

    setList(data ?? []);
    if (response?.meta?.totalItems !== undefined) setTotalItems(response?.meta?.totalItems);
    if (includeStats && setStats && response?.stats) {
      setStats(response?.stats);
    }
    setIsLoading(false);
  }, [
    gridState,
    searchValue,
    startDate,
    endDate,
    showUnassignedOnly,
    showAssignedOnly,
    taskProgressStatus,
    includeStats,
    setStats,
    setList,
    setTotalItems,
    setIsLoading,
  ]);

  useEffect(() => {
    if (showUnassignedOnly || showAssignedOnly) {
      updateGridState((prevState) => {
        if (prevState.ownerFilter && prevState.ownerFilter.length > 0) {
          return {
            ...prevState,
            ownerFilter: [],
            page: 1,
          };
        }
        return prevState;
      });
    } else if (ownerFilterUser && ownerFilterUser?.length > 0 && show) {
      updateGridState((prevState) => ({
        ...prevState,
        ownerFilter: ownerFilterUser,
      }));
    }
  }, [ownerFilterUser, showUnassignedOnly, showAssignedOnly, show, updateGridState]);

  useEffect(() => {
    if (isOpenAplyFilters) return;

    if (showAssignedOnly || showUnassignedOnly) return;

    if (show) {
      fetchTaskList();
    }
  }, [
    show,
    fetchTaskList,
    isOpenAplyFilters,
    searchValue,
    gridState,
    taskProgressStatus,
    showAssignedOnly,
    showUnassignedOnly,
  ]);

  useEffect(() => {
    if (isOpenAplyFilters) return;

    if ((showAssignedOnly || showUnassignedOnly) && show) {
      fetchTaskList();
    }
  }, [show, showAssignedOnly, showUnassignedOnly, isOpenAplyFilters, fetchTaskList]);

  function backToInitialState() {
    if (ownerFilterUser && ownerFilterUser?.length > 0) {
      updateGridState({
        ...initialGridState,
        ownerFilter: ownerFilterUser,
      });
    } else {
      updateGridState(initialGridState);
    }
  }

  const handleChangeRowsPerPage = (rowsPerPage: number) => {
    updateGridState({ rowsPerPage });
  };

  const handlePageChange = (page: number) => {
    updateGridState({ page: page + 1 });
  };

  const handleSortModelChangeCallback = useCallback(
    (newSortModel: GridSortModel) => {
      setSortModel(newSortModel);

      const { field, sort } = newSortModel?.[0] ?? {};
      updateGridState({
        orderBy: field ?? null,
        orderDirection: sort ?? null,
      });
    },
    [updateGridState]
  );

  const handleSortChangeCallback = useCallback(
    (field: string, order: 'asc' | 'desc') => {
      updateGridState({
        orderBy: field ?? null,
        orderDirection: order ?? null,
      });
      return {};
    },
    [updateGridState]
  );

  const handleOnSuccesEdit = () => {
    fetchTaskList();
  };

  const handleOwnerChange: onSelectResource = async (resource) => {
    updateEditModalState({ isSearchMemberModalOpen: false });
    const response = await updateTask(
      {
        ownerId: `${resource?.resourceType}/${resource?.id}`,
        ownerDisplay: getOwnerDisplayLabel(resource),
      },
      taskToEdit?.id as string
    );
    if (!response) {
      enqueueSnackbar('Unable to update Task Owner. Please Try Again', {
        variant: 'error',
      });
      return;
    }
    enqueueSnackbar('Task Owner Succesfully Updated');
    handleOnSuccesEdit();
  };

  if (!show) {
    return null;
  }

  return (
    <Box sx={{ width: '100% !important' }}>
      <Page title={i18n('tasks.title', 'crs')}>
        <Container maxWidth={themeStretch ? false : 'xl'}>
          {includeBreadcrumbs ? (
            <HeaderBreadcrumbs
              title={i18n('tasks.title', 'crs')}
              heading=""
              links={[
                { name: `${i18n('admin.list.dashboard')}`, href: PATH_DASHBOARD.root },
                { name: `${i18n('breadcrumbs.title', 'crs')}` },
                { name: `${i18n('tasks.title', 'crs')}`, href: PATH_DASHBOARD.crs.task },
              ]}
            />
          ) : null}

          <Grid item xs={12} sx={{ marginTop: 4 }}>
            <Card sx={{ width: '100%', paddingTop: 2, paddingX: 1 }}>
              <Grid
                container
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: 2,
                  paddingX: 1,
                }}
                spacing={2}
              >
                <Grid item xs={3}>
                  <SearchTextField
                    value={searchValue}
                    onChange={(event) => {
                      const { value } = event.target;
                      setSearchValue(value);
                      handleSearchChange(value);
                    }}
                    fullWidth
                  />
                </Grid>

                <Grid item xs={6}>
                  <Button
                    sx={{ mt: 1, marginLeft: 'auto' }}
                    variant="contained"
                    onClick={() => {
                      fetchTaskList();
                    }}
                  >
                    {i18n('dashboard.refresh', 'crs')}
                  </Button>
                </Grid>

                <Grid container alignItems="center" justifyContent="flex-end" xs={3}>
                  <Tooltip title="Filter">
                    <IconButton
                      onClick={() => {
                        setIsOpenAplyFilters(true);
                      }}
                      sx={{ height: '40px' }}
                    >
                      <FilterListIcon htmlColor="#919eab" />
                    </IconButton>
                  </Tooltip>
                </Grid>
              </Grid>
              <TaskContextProvider menuTaskHandlers={commonMenuTaskHandlers}>
                <DataGrid
                  sx={{
                    width: '100% !important',
                    '& .MuiDataGrid-columnHeader': { backgroundColor: '#f4f6f8' },
                    '& .MuiDataGrid-row': {
                      cursor: 'pointer',
                      borderBottom: '1px solid #EAECF0',
                      '&:nth-of-type(odd)': {
                        backgroundColor: '#FAFAFA',
                      },
                      '&:hover': {
                        backgroundColor: '#F5F6F7',
                      },
                    },
                  }}
                  rows={list}
                  loading={isLoading}
                  autoHeight={true}
                  getCellClassName={() => 'taskGridBodyCell'}
                  getRowId={({ fhirId }: TaskResponse) => fhirId}
                  getRowSpacing={() => ({ bottom: 5 })}
                  getRowHeight={() => 130}
                  columns={getTaskListColumns({
                    taskStatuses: taskStatus?.asList?.() ?? [],
                    i18n,
                    onTaskOpen: handleTaskDetailsOpen,
                    onEditTask: handleOnTaskEdit,
                    onSearchMemberOpen: handleOnSearchMemberOpen,
                    onCopyTask: handleOnTaskCopy,
                    onAddNoteToTask: handleAddNoteToTask,
                    onSuccessfulEdit: handleOnSuccesEdit,
                    isLoading,
                    handleSortChange: handleSortChangeCallback,
                    onSubTask: handleOnSubTask,
                    permissions: permissions,
                  })}
                  isRowSelectable={() => false}
                  disableColumnFilter
                  disableColumnMenu
                  rowCount={totalItems}
                  pagination
                  paginationMode="server"
                  sortingMode="server"
                  sortModel={sortModel}
                  onSortModelChange={handleSortModelChangeCallback}
                  pageSize={gridState.rowsPerPage}
                  onPageSizeChange={handleChangeRowsPerPage}
                  onPageChange={handlePageChange}
                  rowsPerPageOptions={defaultRowsPerPageOptions}
                  page={gridState.page - 1}
                  components={{
                    Footer: () => (
                      <CustomDataGridPagination
                        page={gridState.page}
                        pageInput={pageInput}
                        onPageInputChange={setPageInput}
                        onPageChange={(newPage) => {
                          updateGridState({ page: newPage });
                          setPageInput('');
                        }}
                        rowsPerPageOptions={defaultRowsPerPageOptions}
                        onPageSizeChange={(newSize) => {
                          updateGridState({ rowsPerPage: newSize, page: 1 });
                          setPageInput('');
                        }}
                      />
                    ),
                  }}
                />
              </TaskContextProvider>
            </Card>
          </Grid>
        </Container>
      </Page>

      <SearchMemberModal
        open={isSearchMemberModalOpen || isSearchMemberFilterModalOpen}
        memberTypesByConfig={searchMemberOptions?.taskSelectOwner ?? []}
        onCancel={() => {
          updateEditModalState({
            isSearchMemberModalOpen: false,
          });
          setIsSearchMemberFilterModalOpen(false);
        }}
        onSelectResource={(resource) => {
          if (isSearchMemberFilterModalOpen) {
            updateGridState(({ ownerFilter }) => ({
              ownerFilter: [
                ...(ownerFilter ?? []),
                { display: getOwnerDisplayLabel(resource), reference: resource?.id },
              ],
            }));
            setIsSearchMemberFilterModalOpen(false);
          } else {
            handleOwnerChange(resource);
          }
        }}
      />

      {patientFromTask ? (
        <ModalCommunication
          isAllowedToAdd={!!permissions?.notes?.isAllowedToAdd}
          // isAllowedToAdd={true}
          open={addNoteToTask}
          patient={patientFromTask}
          onCancel={() => {
            updateEditModalState({ addNoteToTask: false });
          }}
          // carePlan={carePlan}
          resource={taskToEdit}
          typeNote="notes_task"
          i18n={i18n}
        />
      ) : null}

      <TaskDetailDrawer
        onEdit={(task) => {
          updateDrawerState({ isTaskDetailDrawerOpen: false });
          updateEditModalState({
            taskToEdit: task,
            isTaskEditModalOpen: true,
          });
        }}
        open={isTaskDetailDrawerOpen}
        taskId={selectedTaskId}
        onSuccesfulEdit={() => {
          fetchTaskList();
        }}
        onCloseIconButtonClick={() => {
          updateDrawerState({ isTaskDetailDrawerOpen: false });
        }}
        isAllowedToEdit={permissions?.isAllowedToEdit}
        title={i18n('patients.details.tasks.details', 'crs')}
      />
      <TaskModalResolver
        taskToEdit={taskToEdit as WrappedTask}
        taskBasedOn={subTaskBasedOn}
        onSuccessfulEdit={handleOnSuccesEdit}
        onSuccessfulCreation={handleOnSuccesEdit}
        open={isTaskEditModalOpen}
        onClose={() => {
          updateEditModalState({
            taskToEdit: null,
            subTaskBasedOn: null,
            isTaskEditModalOpen: false,
          });
        }}
      />
      <FilterDrawer
        onApplyButtonClick={() => {
          fetchTaskList();
          setIsOpenAplyFilters(false);
        }}
        title={i18n('patients.details.tasks.filterPopUp.title', 'crs')}
        onCloseIconButtonClick={() => {
          setIsOpenAplyFilters(false);
        }}
        onClearAllButtonClick={() => {
          setStartDate(null);
          setEndDate(null);
          backToInitialState();
        }}
        anchor={'right'}
        open={isOpenAplyFilters}
      >
        <TaskFilterList
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={(value) => {
            const endOfDay = value?.endOf('day');
            setEndDate(endOfDay);
          }}
          gridState={gridState}
          updateGridState={updateGridState}
          taskStatus={taskStatus}
          taskType={taskTypes}
          setIsSearchMemberModalOpen={setIsSearchMemberFilterModalOpen}
          i18n={i18n}
        />
      </FilterDrawer>
    </Box>
  );
};
export type OnOwnerSelectionChange = (event: React.SyntheticEvent, newValue: Reference[]) => void;

interface TaskFilterListProps {
  startDate: moment.Moment | null;
  endDate: moment.Moment | null;
  onStartDateChange: onDateChange;
  onEndDateChange: onDateChange;
  gridState: any;
  updateGridState: any;
  taskStatus: any;
  taskType: any;
  setIsSearchMemberModalOpen: any;
  i18n: any;
}

const TaskFilterList = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  gridState,
  updateGridState,
  taskStatus,
  taskType,
  setIsSearchMemberModalOpen,
  i18n,
}: TaskFilterListProps) => (
  <Box py={3}>
    <Box>
      <Grid item sx={{ marginTop: 1 }} xs={5}>
        <Typography fontSize={'1rem'} fontWeight={'bold'} marginBottom={1.4}>
          Status
        </Typography>
        <Autocomplete
          value={gridState.statusFilter}
          multiple
          fullWidth
          onChange={(_: React.SyntheticEvent, taskStatus) => {
            updateGridState((prevGridState: any) => ({
              ...prevGridState,
              statusFilter: taskStatus,
            }));
          }}
          options={taskStatus?.asList() ?? []}
          getOptionLabel={({ display }: ValueSetComposeIncludeConcept) => display ?? ''}
          isOptionEqualToValue={(option, value) => option.code === value.code}
          filterOptions={(options, params) =>
            options?.filter((item) =>
              item?.display?.toLowerCase().includes(params?.inputValue?.toLowerCase())
            )
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label={i18n('patients.details.tasks.filterPopUp.status', 'crs')}
              variant="outlined"
            />
          )}
        />
      </Grid>
    </Box>
    <Box marginTop={3} marginBottom={1}>
      <PeriodFilter
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={onStartDateChange}
        onEndDateChange={onEndDateChange}
      />
    </Box>

    <Box marginTop={3} marginBottom={1}>
      <Grid item xs={3}>
        <Typography fontSize={'1rem'} fontWeight={'bold'} marginBottom={1.4}>
          Task Type
        </Typography>
        <Autocomplete
          value={gridState.typeFilter}
          multiple
          fullWidth
          onChange={(_: React.SyntheticEvent, taskType) => {
            updateGridState((prevGridState: any) => ({
              ...prevGridState,
              typeFilter: taskType,
            }));
          }}
          options={taskType?.asList() ?? []}
          getOptionLabel={({ display }: ValueSetComposeIncludeConcept) => display ?? ''}
          renderInput={(params) => (
            <TextField
              {...params}
              label={i18n('patients.details.tasks.filterPopUp.type', 'crs')}
              variant="outlined"
            />
          )}
        />
      </Grid>
    </Box>

    <Box marginTop={3} marginBottom={1}>
      <Grid item xs={3}>
        <Typography fontSize={'1rem'} fontWeight={'bold'} marginBottom={1.4}>
          Owner
        </Typography>
        <Autocomplete
          open={false}
          value={gridState.ownerFilter}
          multiple
          fullWidth
          sx={{
            '& .MuiAutocomplete-popupIndicator': {
              visibility: 'hidden',
            },
          }}
          onChange={(_: React.SyntheticEvent, owners) => {
            updateGridState((prevGridState: any) => ({
              ...prevGridState,
              ownerFilter: owners,
            }));
          }}
          options={gridState.ownerFilter}
          getOptionLabel={({ display }: Reference) => display ?? ''}
          renderInput={(params) => (
            <TextField
              {...params}
              label={i18n('patients.details.tasks.filterPopUp.owner', 'crs')}
              variant="outlined"
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <Tooltip title="Click here to search by owner">
                      <IconButton
                        onClick={() => {
                          setIsSearchMemberModalOpen(true);
                        }}
                      >
                        <PersonSearchIcon />
                      </IconButton>
                    </Tooltip>
                    {params.InputProps.startAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </Grid>
    </Box>
  </Box>
);
export default TaskList;
